/**
 * Chests Store
 *
 * Source de vérité DURABLE des coffres à ouvrir, par enfant.
 *
 * Historiquement le seul chemin vers un coffre était sa notification. Fermer la
 * modale sans réclamer (un swipe vers le bas suffit sur un sheet à breakpoint 0)
 * supprimait la ligne `user_notifications` alors que le coffre restait
 * `is_claimed = false` : le coffre existait encore en base mais plus rien ne
 * pouvait le montrer. 31 coffres sur 35 non réclamés étaient dans cet état en
 * prod.
 *
 * Depuis, les deux rôles sont séparés :
 * - la notification est une CÉLÉBRATION, jetable, qui peut disparaître ;
 * - ce store est le STOCK, alimenté par `chests.is_claimed = false`, et c'est
 *   lui qui alimente le bloc de la page Récompenses et le compteur de son onglet.
 *
 * Aucun canal Realtime propre : la notification `chest_available` créée par le
 * trigger DB à l'INSERT arrive déjà en direct, y compris depuis un autre
 * appareil, et sert de signal de rafraîchissement.
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { chestService } from '@/services/chest.service';
import type { Chest } from '@/models/Chest';
import { useAppContextStore } from './appContext';

export const useChestsStore = defineStore('chests', () => {
  // childId -> coffres non réclamés, du plus ancien au plus récent.
  const unclaimed = ref<Map<string, Chest[]>>(new Map());

  /**
   * Coffre que l'utilisateur a demandé à ouvrir depuis la pilule. Lu par
   * RewardNotificationQueue, qui héberge la ChestModal pour toute l'app.
   * Distinct du chemin notification : ici il n'y a aucune ligne à supprimer.
   */
  const requestedChest = ref<Chest | null>(null);

  function chestsFor(childId: string | null | undefined): Chest[] {
    if (!childId) return [];
    return unclaimed.value.get(childId) ?? [];
  }

  function countFor(childId: string | null | undefined): number {
    return chestsFor(childId).length;
  }

  function setChests(childId: string, chests: Chest[]): void {
    const next = new Map(unclaimed.value);
    if (chests.length) next.set(childId, chests);
    else next.delete(childId);
    unclaimed.value = next;
  }

  /** L'enfant dont la top bar parle : la vue enfant l'emporte, sinon l'enfant courant. */
  const targetChildId = computed(() => {
    const appContext = useAppContextStore();
    return appContext.childViewChildId ?? appContext.currentChildId ?? null;
  });

  const currentCount = computed(() => countFor(targetChildId.value));

  /** Coffres non réclamés d'un enfant (changement d'enfant, après une génération). */
  async function loadForChild(childId?: string): Promise<void> {
    const id = childId ?? targetChildId.value;
    if (!id) return;
    try {
      const chests = await chestService.getUnclaimedChests(id);
      // Le service rend le plus récent en premier ; on ouvre le plus ancien
      // d'abord, pour que la file se vide dans l'ordre où elle s'est remplie.
      setChests(id, [...chests].reverse());
    } catch (err) {
      // Jamais bloquant : sans stock chargé la pilule reste masquée, l'app
      // fonctionne exactement comme avant.
      console.error('[chests] loadForChild failed:', err);
    }
  }

  /** Coffres non réclamés de tout le foyer, en une requête (hydratation du boot). */
  async function loadForChildren(childIds: string[]): Promise<void> {
    if (!childIds.length) return;
    const byChild = await chestService.getUnclaimedChestsForChildren(childIds);
    const next = new Map(unclaimed.value);
    for (const id of childIds) {
      const chests = byChild.get(id) ?? [];
      if (chests.length) next.set(id, chests);
      else next.delete(id);
    }
    unclaimed.value = next;
  }

  /** Demande l'ouverture du plus ancien coffre en attente de l'enfant en contexte. */
  function openNext(childId?: string): void {
    const id = childId ?? targetChildId.value;
    const next = chestsFor(id)[0];
    if (next) requestedChest.value = next;
  }

  function clearRequest(): void {
    requestedChest.value = null;
  }

  /**
   * Retire un coffre réclamé du stock, sans aller-retour serveur.
   * Idempotent : appelable depuis les deux chemins d'ouverture.
   */
  function markClaimed(chestId: string): void {
    const next = new Map(unclaimed.value);
    let changed = false;
    for (const [childId, chests] of next) {
      const filtered = chests.filter(c => c.id !== chestId);
      if (filtered.length === chests.length) continue;
      changed = true;
      if (filtered.length) next.set(childId, filtered);
      else next.delete(childId);
    }
    if (changed) unclaimed.value = next;
    if (requestedChest.value?.id === chestId) requestedChest.value = null;
  }

  /** Ajoute un coffre fraîchement généré au stock (idempotent sur l'id). */
  function noteGenerated(chest: Chest): void {
    if (chest.is_claimed) return;
    const existing = chestsFor(chest.child_id);
    if (existing.some(c => c.id === chest.id)) return;
    setChests(chest.child_id, [...existing, chest]);
  }

  function reset(): void {
    unclaimed.value = new Map();
    requestedChest.value = null;
  }

  return {
    // State
    unclaimed,
    requestedChest,
    // Computed
    currentCount,
    targetChildId,
    // Actions
    chestsFor,
    countFor,
    loadForChild,
    loadForChildren,
    openNext,
    clearRequest,
    markClaimed,
    noteGenerated,
    reset,
  };
});
